import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { CadsAmountDetailBitComponent } from '../../bits/cads-amount-detail-bit/cads-amount-detail-bit.component';
import { CadsHeadingDetailBitComponent } from '../../bits/cads-heading-detail-bit/cads-heading-detail-bit.component';
import { CadsIconViewBitComponent } from '../../bits/cads-icon-view-bit/cads-icon-view-bit.component';
import type {
  CadsIconViewBitConfig,
  CadsIconViewBitShape,
} from '../../bits/cads-icon-view-bit/cads-icon-view-bit.config';

import { CadsAccountRowConfig } from './cads-account-row.config';

/**
 * Horizontal **account line**: optional {@link CadsIconViewBitComponent} (tile vs bare glyph follows heading content unless
 * overridden), {@link CadsHeadingDetailBitComponent}, and optional {@link CadsAmountDetailBitComponent}. **No** border,
 * click handling, or outer inset—wrap with {@link CadsTapViewComponent} (optional trailing chevron when `tappable`) or
 * {@link CadsCardViewComponent} and listen for `tapped` on the container; those shells provide horizontal and vertical
 * padding for the row.
 *
 * @see {@link CadsAccountRowConfig} — `accountRowHeading`, optional `accountRowLeading`, and `accountRowDetails` use the
 * same types as the underlying bits.
 */
@Component({
    selector: 'cads-account-row',
    imports: [CadsIconViewBitComponent, CadsHeadingDetailBitComponent, CadsAmountDetailBitComponent],
    templateUrl: './cads-account-row.component.html',
    styleUrl: './cads-account-row.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CadsAccountRowComponent {
  readonly config = input.required<CadsAccountRowConfig>();

  /**
   * Leading icon config with defaults: **rounded** tile when eyebrow or subtitle is present, else **icon** (bare); **size**
   * matches {@link CadsAccountRowConfig.accountRowHeading} when omitted on the icon config.
   */
  protected readonly resolvedLeadingIconConfig = computed((): CadsIconViewBitConfig | null => {
    const cfg = this.config();
    const lead = cfg.accountRowLeading;
    if (!lead || lead.kind !== 'icon') {
      return null;
    }
    const heading = cfg.accountRowHeading;
    const base = lead.config;
    const implicitTile = !!(heading.eyebrow?.trim() || heading.subtitle?.trim());
    const implicitShape: CadsIconViewBitShape = implicitTile ? 'rounded' : 'icon';
    const rowSize = heading.size ?? 'medium';
    return {
      ...base,
      shape: base.shape ?? implicitShape,
      size: base.size ?? rowSize,
    };
  });
}
